import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useQuery } from "react-query";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";

import SearchDateFilter from "../../../components/common/SearchDateFilter";
import SubmissionRequestsSummary from "../../../components/common/SubmissionRequestsSummary";
import SelectFieldComponent from "../../../components/common/SelectFieldComponent";
import Table from "../../../components/common/KycSubmissionTable";
import { ACCOUNTTYPES } from "../../../utils/constants";
import { formatStartDate, formatEndDate } from "../../../utils/helpers";
import {
	getAllKycSubmissionRequestsSummary,
	getAllKycSubmission,
} from "../../../services/admin";

const selectFieldWidth = "70%";

const Dashboard = () => {
	const [searchValue, setSearchValue] = useState("");
	const [displayedRequests, setDisplayedRequests] = useState([]);
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);
	const [accountType, setAccountType] = useState("");
	const [allReports, setAllReports] = useState([]);

	const {
		data: summary = {},
		isLoading: summaryLoading,
		isError: summaryError,
		error: summaryErrorMessage,
	} = useQuery(
		["get-kycSubmissionSummary"],
		async () => getAllKycSubmissionRequestsSummary(),
		{
			select: (data) => data.data,
			staleTime: 4 * 60 * 1000,
		}
	);

	const {
		data: allRequests = [],
		isLoading: allRequestLoading,
		isError: allRequestError,
		error: allRequestErrorMessage,
	} = useQuery(
		["get-allKycSubmissionRequest"],
		async () => getAllKycSubmission(),
		{
			select: (data) => data.data,
			staleTime: 4 * 60 * 1000,
		}
	);

	useEffect(() => {
		if (summaryError) {
			if (summaryErrorMessage.response) {
				toast.error(summaryErrorMessage.response.data?.message);
			} else if (summaryErrorMessage.request) {
				toast.error("Internal Server Error");
			} else {
				toast.error("Error", summaryErrorMessage.message);
			}
		}
		if (allRequestError) {
			if (allRequestErrorMessage.response) {
				toast.error(allRequestErrorMessage.response.data?.message);
			} else if (allRequestErrorMessage.request) {
				toast.error("Internal Server Error");
			} else {
				toast.error("Error", allRequestErrorMessage.message);
			}
		}
	}, [
		summaryError,
		summaryErrorMessage,
		allRequestError,
		allRequestErrorMessage,
	]);

	useEffect(() => {
		if (!startDate || !endDate) {
			let filteredReport = [...allReports];
			if (searchValue) {
				const filtered = filteredReport.filter(
					(item) =>
						item.firstname?.toLowerCase().includes(searchValue.toLowerCase()) ||
						item.middlename
							?.toLowerCase()
							.includes(searchValue.toLowerCase()) ||
						item.lastname?.toLowerCase().includes(searchValue.toLowerCase())
				);
				filteredReport = filtered;
			}
			if (accountType) {
				const filtered = filteredReport.filter(
					(item) => item.accountType === accountType
				);
				filteredReport = filtered;
			}
			setDisplayedRequests(filteredReport);
		}
		if (startDate && endDate) {
			let filteredReport = [...allReports];
			if (searchValue) {
				const filtered = filteredReport.filter(
					(item) =>
						item.firstname?.toLowerCase().includes(searchValue.toLowerCase()) ||
						item.middlename
							?.toLowerCase()
							.includes(searchValue.toLowerCase()) ||
						item.lastname?.toLowerCase().includes(searchValue.toLowerCase())
				);
				filteredReport = filtered;
			}
			if (accountType) {
				const filtered = filteredReport.filter(
					(item) => item.accountType === accountType
				);
				filteredReport = filtered;
			}
			const filteredReportDate = filteredReport.filter(
				(item) =>
					new Date(item.createdAt) >= formatStartDate(startDate) &&
					new Date(item.createdAt) <= formatEndDate(endDate)
			);
			filteredReport = filteredReportDate;
			setDisplayedRequests(filteredReport);
		}
	}, [searchValue, startDate, endDate, accountType, allReports]);

	useEffect(() => {
		if (allRequests.length > 0 && !allRequestLoading) {
			setDisplayedRequests(allRequests);
			setAllReports(allRequests);
		}
	}, [allRequests, allRequestLoading]);

	const setdefaultState = () => {
		setSearchValue("");
		setStartDate(null);
		setEndDate(null);
		setAccountType("");
	};
	return (
		<Box sx={{ padding: { md: "2rem", xs: "1rem" } }}>
			<Box sx={{ mb: "1rem" }}>
				<Typography
					variant="h6"
					component="div"
					sx={{ textTransform: "capitalize", margin: "0.5rem" }}
				>
					Dashboard
				</Typography>
				<SubmissionRequestsSummary
					summaryLoading={summaryLoading}
					requestSummary={summary}
				/>
			</Box>

			<SearchDateFilter
				searchValue={searchValue}
				setSearchValue={setSearchValue}
				startDate={startDate}
				setStartDate={setStartDate}
				endDate={endDate}
				setEndDate={setEndDate}
			/>
			<Box
				sx={{
					display: "flex",
					flexDirection: { md: "row", xs: "column" },
					mb: "1rem",
				}}
			>
				<Box
					sx={{
						width: {
							md: "50%",
							xs: "100%",
						},
						mb: {
							md: 0,
							xs: "1rem",
						},
					}}
				>
					<SelectFieldComponent
						width={selectFieldWidth}
						values={ACCOUNTTYPES}
						value={accountType}
						onChange={(e) => setAccountType(e.target.value)}
						label="Account Type"
						id="accountType"
					/>
				</Box>

				<Box
					sx={{
						ml: { md: "auto", xs: 0 },
						display: "flex",
						flexDirection: { md: "row", xs: "column" },
					}}
				>
					<Box>
						<Button
							className="reset-all"
							variant="contained"
							onClick={() => {
								setDisplayedRequests(allReports);
								setdefaultState();
							}}
						>
							Reset All
						</Button>
					</Box>
				</Box>
			</Box>

			<Box
				sx={{ boxShadow: "0 3px 10px rgb(0 0 0 / 0.2)", paddingTop: "1rem" }}
			>
				<Typography
					variant="h6"
					component="div"
					sx={{ textTransform: "capitalize", margin: "0.5rem" }}
					gutterBottom
				>
					Recent Requests
				</Typography>

				<Table loading={allRequestLoading} tableData={displayedRequests} />
			</Box>
		</Box>
	);
};

export default Dashboard;
